/**
 * AB#21611 / AB#21609 (Zendesk#9200) - sample-upload size limits.
 *
 * The sample modal posts a front and a back slot per factory product, so a 12-product order
 * sends 24 files in one multipart request. PHP discards every upload past `max_file_uploads`
 * *silently* - no warning, no error - and the request used to fail with a bare 500. Three
 * orders and two users hit this between 28 Jul and 16 Sep 2026.
 *
 * The backend now rejects a truncated submission with a 422 explaining the loss, but that
 * only arrives after the merchant has uploaded tens of megabytes. These helpers let the UI
 * say so up front instead.
 *
 * Keep MAX_SAMPLE_FILES in step with the server's `max_file_uploads`. The server value is
 * authoritative; this is the client-side mirror of it, overridable per environment so the
 * two can be changed together without a code release.
 */

const DEFAULT_MAX_SAMPLE_FILES = 40;

const parsed = Number(import.meta.env.VITE_MAX_SAMPLE_FILES);

/** Maximum files a single sample submission may contain. Mirrors PHP `max_file_uploads`. */
export const MAX_SAMPLE_FILES: number =
  Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX_SAMPLE_FILES;

/** Products per submission: each contributes a front and a back slot. */
export const MAX_SAMPLE_PRODUCTS: number = Math.max(1, Math.floor(MAX_SAMPLE_FILES / 2));

/**
 * Count the files that will actually be uploaded.
 *
 * Only slots holding a real File count. A slot the merchant left empty is serialised as an
 * empty string, which travels as an ordinary POST field and does not consume an upload slot -
 * counting those would overstate the total and warn people who are nowhere near the limit.
 */
export function countSampleUploads(items: unknown): number {
  if (!Array.isArray(items)) {
    return 0;
  }

  return items.reduce((total: number, item: any) => {
    const files = Array.isArray(item?.files) ? item.files : [];

    return (
      total +
      files.filter((slot: any) => slot?.file instanceof File || slot?.file instanceof Blob).length
    );
  }, 0);
}

/**
 * Human-readable reason a submission is too large, or null when it fits.
 * Mirrors the backend's 422 wording so merchants see one consistent explanation.
 */
export function sampleUploadLimitError(items: unknown): string | null {
  const count = countSampleUploads(items);

  if (count <= MAX_SAMPLE_FILES) {
    return null;
  }

  return (
    `You have attached ${count} sample files, but a single submission can include at most ` +
    `${MAX_SAMPLE_FILES}. Please submit samples for about ${MAX_SAMPLE_PRODUCTS} products at a ` +
    `time (each product uses 2 files, front and back).`
  );
}
